import React, { useCallback, useContext, useMemo } from "react";
import { ModuleWrapper } from "components/fragments";
import { HeaderA } from "components/headers";
import { DataTable, Icon, Text } from "components/commons";
import { useApi, useFilter, useMount } from "hooks";
import { mapObject } from "services";
import columns from "./columns";
import "./mdmListTable.css";
import lang from "translations";
import { VenueContext } from "contexts";
import MdmFilter from "./mdmfilter";
import { mdmFilterState } from "./filters";
import {
  mdmListRequest,
  mdmListResponse,
} from "mappers/mdm.mapper";
import {
  searchMdmData,  
  searchMdmExport
} from "apis/mdm.api";

const MdmList = () => {
  const { venue } = useContext(VenueContext);

  const {
    request: searchMdmRequest,
    error,
    mappedData,
    result: searchMdmResult = {
      data: [],
      metadata: { total: 0 },
    },
    loading,
  } = useApi({
    api: searchMdmData,
    isArray: true,
    handleOwnError: true,
    mapper: mdmListResponse,
  });

  const { modifyFilter, clearFilter, filterState, requestState, modifyFilters } =
    useFilter(
      mdmFilterState(venue)
    );

  const searchMdm = useCallback(
    async (requestState) => {
      await searchMdmRequest(
        mapObject(requestState, mdmListRequest)
      );
    },
    [searchMdmRequest]
  );

  useMount(() => {
    searchMdm(requestState);
  });


  const applyFilterCb = useCallback(
    async (searchKey) => {
      const { requestState } = modifyFilters({
        page: 1,
        searchKey,
      });
      await searchMdm(requestState);
    },
    [searchMdm, modifyFilters]
  );

  const clearFilterCb = useCallback(() => {
    const { requestState } = clearFilter();
    searchMdm(requestState);
  }, [clearFilter, searchMdm]);

 
  const {
    request: searchMdmReportExportRequest,
    loading: searchMdmReportExportLoading,
  } = useApi({
    api: searchMdmExport,
  });

  const exportXlsx = useCallback(() => {
    searchMdmReportExportRequest(
      mapObject({ ...requestState }, mdmListRequest)
    );
  }, [searchMdmReportExportRequest, requestState]);

  const renderDate = useCallback(
    (dateCreated) => (
      <div>
        <Text className="w-20 max-w-20 md:w-32.5 md:max-w-32.5">{dateCreated?.date}</Text>
        {dateCreated?.time && <Text color="text-gray">{dateCreated.time}</Text>}
      </div>
    ),
    []
  );

  const renderLocation = useCallback(
    (longitude, latitude) => (
      <div>
        <Text className="w-20 max-w-20 md:w-32.5 md:max-w-32.5">{longitude}</Text>
        {latitude && <Text color="w-20 max-w-20 md:w-32.5 md:max-w-32.5">{latitude}</Text>}
      </div>
    ),
    []
  );

  const renderData = useMemo(() => {
    return mappedData.map(({ deviceName, deviceImei, model, venueName, longitude, latitude, osVersion, firmwareVersion, softwareVersion, ghlVersion, ttbVersion, userDetails, deviceRegisterDateTime, deviceLastLocationDateTime }) => {
      return {
        deviceName: deviceName,
        deviceImei: <Text tagUid>{deviceImei}</Text>,
        model: model,
        venueName: venueName,
        location: renderLocation(longitude, latitude),
        osVersion: osVersion,
        firmwareVersion: firmwareVersion,
        softwareVersion: softwareVersion,
        ghlVersion: ghlVersion,
        ttbVersion: ttbVersion,
        userDetails: userDetails,
        deviceRegisterDateTime: renderDate(deviceRegisterDateTime),
        deviceLastLocationDateTime: renderDate(deviceLastLocationDateTime),
      };
    });
  }, [mappedData, renderDate, renderLocation]);

  return (
    <ModuleWrapper
      header={
        <HeaderA
          title={lang.mdmDeviceList}
          description={lang.mdmDeviceListDescription}
          button={{
            iconPrefix: <Icon className="mr-sm" name="download" paddingless fontSize={12} />,
            text: lang.exportXlsx,
            loading: searchMdmReportExportLoading,
            disabled: searchMdmReportExportLoading,
            onClick: () => {
              exportXlsx();
            },
          }}
          className="mb-md"
        />
      }
    >
      <>
        <MdmFilter
          filterState={filterState}
          requestState={requestState}
          modifyFilter={modifyFilter}
          modifyFilters={modifyFilters}
          applyFilter={applyFilterCb}
          clearFilter={clearFilterCb}
        />
        <div className="mdm-list-table">
          <DataTable
          page={filterState.page}
          pageSize={filterState.pageSize}
          onChangePage={modifyFilters}
          fetchList={searchMdm}
          total={searchMdmResult?.metadata?.total || 0}
          loading={loading}
          columns={columns}
          data={renderData}
          error={error}
          />
        </div>
      </>
    </ModuleWrapper>
  );
};

export default MdmList;
